// Scheme allowlist for user- or admin-supplied URLs bound into `<a :href>`.
// `javascript:` and `data:` URLs execute in the origin of the current
// document; if we render them unfiltered we hand every visitor a stored-XSS
// primitive. Every scheme not in this list is rejected.
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:", "tel:"];

// What we substitute when a URL fails the guard. `#` matches the fallback
// pattern already used by `getFooterLink()` — click becomes a no-op page
// anchor rather than a broken navigation.
const SAFE_FALLBACK = "#";

// Regex for an RFC 3986 scheme prefix. Used to distinguish "absolute URL
// with a scheme" from "bare hostname or relative path".
const SCHEME_PREFIX = /^[a-z][a-z0-9+.\-]*:/i;

// Returns `url` if it uses an allowed scheme or is a safe same-origin
// relative reference. Returns `#` otherwise. Callers should always bind
// the result, not the raw input, into `:href`.
export function safeHref(url) {
  if (typeof url !== "string") return SAFE_FALLBACK;
  const trimmed = url.trim();
  if (trimmed.length === 0) return SAFE_FALLBACK;

  // Protocol-relative URLs (`//host/path`) resolve to the current page's
  // scheme. Reject them so an attacker can't smuggle an external navigation
  // through a value the caller expected to be a local path.
  if (trimmed.startsWith("//")) return SAFE_FALLBACK;

  // Same-origin relative references: root-relative paths, path-relative
  // segments, query-only, and fragment-only. None of these can carry a
  // scheme, so they can't be a javascript:/data: vector.
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../") ||
    trimmed.startsWith("?") ||
    trimmed.startsWith("#")
  ) {
    return trimmed;
  }

  // A leading scheme means we must parse and check it. Anything without a
  // scheme that reached this point is a bare token (`example.com`,
  // `foo bar`) and is not safe to render as a link.
  if (!SCHEME_PREFIX.test(trimmed)) return SAFE_FALLBACK;

  try {
    const parsed = new URL(trimmed);
    if (ALLOWED_SCHEMES.includes(parsed.protocol)) return trimmed;
  } catch (_) {
    // Unparseable — fall through to fallback.
  }
  return SAFE_FALLBACK;
}

// True iff `url` is a well-formed absolute URL with an allowed scheme.
// Used by header.vue to decide between `<a :href>` (external navigation)
// and `<router-link :to>` (SPA route). The prior implementation used
// `new URL()` alone, which returned true for `javascript:` and `data:` and
// actively routed those into the vulnerable `<a>` branch.
export function isAllowedExternalUrl(url) {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  if (trimmed.length === 0) return false;
  try {
    const parsed = new URL(trimmed);
    return ALLOWED_SCHEMES.includes(parsed.protocol);
  } catch (_) {
    return false;
  }
}
